import { tokenManager } from 'common/api/tokenManager';
import { apiFetch } from 'common/api/utils';
import { LOGIN_URL } from 'common/api/config';
import { APIResponse } from 'common/api/models';
import {
    RefreshTokenResponse,
    RefreshTokenResult,
} from 'common/api/users/models/RefreshTokenResponse';
import {
    createAuthCountryValidationErrorResponse,
    validateAuthCountryFromAuthResponse,
} from 'common/api/authCountry';
import { AuthResponse } from 'common/api/users/models/AuthResponse';

/**
 * Refresh tokens using the correct server based on how the user logged in.
 * - authSource 'api': refresh via the Auth API (username/password login)
 * - authSource 'oidc': refresh via the OIDC IdentityServer (Google/Apple social login)
 */
export const refreshToken = async (): Promise<
    APIResponse<RefreshTokenResponse, RefreshTokenResponse>
> => {
    const tokens = await tokenManager.getTokens();

    if (!tokens.user.refreshToken) {
        return {
            error: {
                response: null,
                error: new Error('No refresh token available'),
            },
        };
    }

    if (tokens.user.authSource === 'oidc') {
        return refreshViaOidc(tokens.user.refreshToken);
    }

    // Default to API refresh (covers 'api' and legacy tokens without authSource)
    return refreshViaApi(tokens.user);
};

/**
 * Refresh via the Auth API (POST /login/refresh)
 */
const refreshViaApi = async (userTokens: {
    token: string;
    refreshToken: string;
    inactivityTimeoutMinutes: number | null;
}): Promise<APIResponse<RefreshTokenResponse, RefreshTokenResponse>> => {
    const response = await apiFetch<RefreshTokenResponse, RefreshTokenResponse>(
        `${LOGIN_URL}/refresh`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            // The endpoint requires a present Token field even though only
            // RefreshToken is actually validated. clearSession() intentionally
            // blanks the stale access token on logout, so substitute a
            // non-secret placeholder for this request only — never persisted,
            // never the real (cleared) credential.
            body: JSON.stringify({ ...userTokens, token: userTokens.token || 'cleared' }),
        }
    );

    if (response.error) {
        return response;
    }

    const authResponse: AuthResponse = {
        token: response.data.token,
        refreshToken: response.data.refreshToken,
        inactivityTimeoutMinutes: response.data.inactivityTimeoutMinutes,
        authSource: 'api',
    };

    const countryValidation = validateAuthCountryFromAuthResponse(authResponse);
    if (!countryValidation.isValid) {
        return createAuthCountryValidationErrorResponse(countryValidation, response.response);
    }

    await tokenManager.setTokens(authResponse);

    return response;
};

/**
 * Refresh via the OIDC IdentityServer (/connect/token with grant_type=refresh_token)
 */
const refreshViaOidc = async (
    refreshTokenValue: string
): Promise<APIResponse<RefreshTokenResponse, RefreshTokenResponse>> => {
    const { refreshOidcToken } = await import('common/api/oidc');
    const response = await refreshOidcToken(refreshTokenValue);

    if (response.error) {
        return {
            error: {
                response: null,
                error: new Error(response.errorMessage || 'Token refresh failed'),
            },
        };
    }

    const authResponse: AuthResponse = {
        token: response.data!.token,
        refreshToken: response.data!.refreshToken,
        idToken: response.data!.idToken,
        inactivityTimeoutMinutes: response.data!.inactivityTimeoutMinutes,
        authSource: 'oidc',
    };

    const countryValidation = validateAuthCountryFromAuthResponse(authResponse);
    if (!countryValidation.isValid) {
        return createAuthCountryValidationErrorResponse(countryValidation);
    }

    // Lazy import keeps the scope check out of the web bundle, which never refreshes via OIDC.
    const { enforceRequestedScopes } = await import('common/api/oidc/scopeReauth');
    const reauthError = await enforceRequestedScopes(
        authResponse.token,
        response.scopes,
        authResponse.refreshToken
    );
    if (reauthError) {
        return { error: { response: null, error: reauthError } };
    }

    await tokenManager.setTokens(authResponse);

    return {
        data: {
            token: response.data!.token,
            refreshToken: response.data!.refreshToken,
            inactivityTimeoutMinutes: response.data!.inactivityTimeoutMinutes,
            result: RefreshTokenResult.Success,
        },
    };
};
